import { useTranslation } from "react-i18next";
import { Info } from "lucide-react";
import type { AtlasConfig } from "./OrderAtlasConfig";
import type { ValidConfig } from "./OrderValidConfig";
import { usePricingData } from "@/hooks/usePricingData";

interface Props {
  product: "atlas" | "valid" | null;
  atlasConfig: AtlasConfig;
  validConfig: ValidConfig;
}

/** Parse a price string like "1 795 SEK" or "695 SEK" into a number */
function parsePrice(priceStr: string): number {
  const cleaned = priceStr.replace(/[^\d,\.]/g, "").replace(",", ".").replace(/\s/g, "");
  return parseFloat(cleaned) || 0;
}

const ROUTING_PRICES: Record<string, number> = {
  none: 0,
  small: 495,
  medium: 995,
  large: 1495,
};

/** Map internal plan keys to DB plan names for lookup */
const PLAN_NAME_MAP: Record<string, string> = {
  medium3: "medium 3-mån",
};

export function OrderSummary({ product, atlasConfig, validConfig }: Props) {
  const { t } = useTranslation();
  const { plans: validPlans, addons: validAddons } = usePricingData("valid");
  const { plans: atlasPlans, addons: atlasAddons } = usePricingData("atlas");

  if (!product) return null;

  const bindingMonths = product === "valid" ? 3
    : (atlasConfig.plan === "medium3" ? 3 : 12);

  const lines: { label: string; amount: string; included?: string[] }[] = [];
  let total = 0;

  if (product === "atlas") {
    const planLookup = PLAN_NAME_MAP[atlasConfig.plan.toLowerCase()] || atlasConfig.plan.toLowerCase();
    const plan = atlasPlans.find((p) => p.name.toLowerCase() === planLookup);
    const planPrice = plan ? parsePrice(plan.price) : 0;
    lines.push({
      label: `${plan?.name || atlasConfig.plan} ${t("order.basePlan")}`,
      amount: `${planPrice.toLocaleString("sv-SE")} SEK`,
      included: plan?.features ?? [],
    });
    total += planPrice;

    // Extra admin users (1 included)
    const extraAdmins = Math.max(0, atlasConfig.adminUsers - 1);
    if (extraAdmins > 0) {
      const addonData = atlasAddons.find((a) => a.name.toLowerCase().includes("admin"));
      const addonPrice = addonData && plan ? parsePrice(addonData.prices[plan.name] || "0") : parsePrice(t("productPages.atlas.pricing.addons.extraAdmin.price"));
      const lineTotal = extraAdmins * addonPrice;
      lines.push({
        label: `${extraAdmins} × ${addonData?.name || t("productPages.atlas.pricing.addons.extraAdmin.name")}`,
        amount: `${lineTotal.toLocaleString("sv-SE")} SEK`,
      });
      total += lineTotal;
    }

    // Viewer users
    if (atlasConfig.viewerUsers > 0) {
      const addonData = atlasAddons.find((a) => a.name.toLowerCase().includes("läsare") || a.name.toLowerCase().includes("viewer"));
      const addonPrice = addonData && plan ? parsePrice(addonData.prices[plan.name] || "0") : parsePrice(t("productPages.atlas.pricing.addons.extraViewer.price"));
      const lineTotal = atlasConfig.viewerUsers * addonPrice;
      lines.push({
        label: `${atlasConfig.viewerUsers} × ${addonData?.name || t("productPages.atlas.pricing.addons.extraViewer.name")}`,
        amount: `${lineTotal.toLocaleString("sv-SE")} SEK`,
      });
      total += lineTotal;
    }

    // Extra maps (1 included)
    const extraMaps = Math.max(0, atlasConfig.maps - 1);
    if (extraMaps > 0) {
      const addonData = atlasAddons.find((a) => a.name.toLowerCase().includes("kart") || a.name.toLowerCase().includes("map"));
      const addonPrice = addonData && plan ? parsePrice(addonData.prices[plan.name] || "0") : parsePrice(t("productPages.atlas.pricing.addons.extraMap.price"));
      const lineTotal = extraMaps * addonPrice;
      lines.push({
        label: `${extraMaps} × ${addonData?.name || t("productPages.atlas.pricing.addons.extraMap.name")}`,
        amount: `${lineTotal.toLocaleString("sv-SE")} SEK`,
      });
      total += lineTotal;
    }

    // Extra countries (1 included)
    const extraCountries = Math.max(0, atlasConfig.countries.length - 1);
    if (extraCountries > 0) {
      const addonData = atlasAddons.find((a) => a.name.toLowerCase().includes("land") || a.name.toLowerCase().includes("countr"));
      const addonPrice = addonData && plan ? parsePrice(addonData.prices[plan.name] || "0") : parsePrice(t("productPages.atlas.pricing.addons.extraCountry.price"));
      const lineTotal = extraCountries * addonPrice;
      lines.push({
        label: `${extraCountries} × ${addonData?.name || t("productPages.atlas.pricing.addons.extraCountry.name")}`,
        amount: `${lineTotal.toLocaleString("sv-SE")} SEK`,
      });
      total += lineTotal;
    }

    // Routing add-on
    if (atlasConfig.routing && atlasConfig.routing !== "none") {
      const routingPrice = ROUTING_PRICES[atlasConfig.routing] || 0;
      const includedInMedium = atlasConfig.plan === "medium" || atlasConfig.plan === "medium3";
      const routingSmallValue = ROUTING_PRICES.small;
      const effectivePrice = includedInMedium
        ? Math.max(0, routingPrice - routingSmallValue)
        : routingPrice;

      if (includedInMedium && atlasConfig.routing === "small") {
        lines.push({
          label: `Routing Small (${t("order.routing.included")})`,
          amount: "0 SEK",
        });
      } else {
        lines.push({
          label: `Routing ${atlasConfig.routing.charAt(0).toUpperCase() + atlasConfig.routing.slice(1)}${includedInMedium ? ` (${t("order.routing.upgradeNote")})` : ""}`,
          amount: `${effectivePrice.toLocaleString("sv-SE")} SEK`,
        });
        total += effectivePrice;
      }
    }
  } else {
    // Valid
    const plan = validPlans.find((p) => p.name.toLowerCase() === validConfig.plan.toLowerCase());
    const planPrice = plan ? parsePrice(plan.price) : 0;

    // Build included bullets: features + combined volume/overage rows from details
    const included: string[] = [...(plan?.features ?? [])];
    if (plan?.details) {
      const findVal = (re: RegExp) => plan.details.find((d) => re.test(d.label))?.value;
      const valVol = findVal(/valider/i);
      const valOver = findVal(/överskj.*valider/i);
      const acVol = findVal(/autocomplete/i);
      const acOver = findVal(/överskj.*autocomplete/i);
      if (valVol) {
        included.push(
          valOver
            ? `${valVol} valideringar/mån (${valOver}/överskjutande)`
            : `${valVol} valideringar/mån`
        );
      }
      if (acVol) {
        included.push(
          acOver
            ? `${acVol} autocomplete/mån (${acOver}/överskjutande)`
            : `${acVol} autocomplete/mån`
        );
      }
    }

    lines.push({
      label: `${plan?.name || validConfig.plan} ${t("order.basePlan")}`,
      amount: `${planPrice.toLocaleString("sv-SE")} SEK`,
      included,
    });
    total += planPrice;

    // Addons listed as per-unit prices
    validConfig.addons.forEach((addonName) => {
      const addonData = validAddons.find((a) => a.name === addonName);
      const priceStr = addonData && plan ? (addonData.prices[plan.name] || "–") : "–";
      lines.push({
        label: addonName,
        amount: priceStr,
      });
    });
  }

  return (
    <div className="sticky top-24 p-6 rounded-xl border border-border bg-card space-y-4">
      <h3 className="text-lg font-semibold text-foreground">{t("order.summary")}</h3>

      <div className="space-y-3">
        {lines.map((line, i) => (
          <div key={i}>
            <div className="flex justify-between text-sm gap-3">
              <span className="text-muted-foreground">{line.label}</span>
              <span className="font-medium text-foreground whitespace-nowrap">{line.amount}</span>
            </div>
            {line.included && line.included.length > 0 && (
              <ul className="mt-1.5 ml-1 space-y-1">
                {line.included.map((item, j) => (
                  <li key={j} className="text-xs text-muted-foreground flex gap-2">
                    <span className="text-coral shrink-0">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>

      {product === "atlas" && (
        <div className="border-t border-border pt-3 flex justify-between">
          <span className="font-semibold text-foreground">{t("order.total")}</span>
          <span className="font-bold text-lg text-primary">
            {total.toLocaleString("sv-SE")} SEK/{t("order.perMonthShort")}
          </span>
        </div>
      )}

      {product === "valid" && validConfig.addons.length > 0 && (
        <p className="text-xs text-muted-foreground italic">
          {t("order.validAddonsNote")}
        </p>
      )}

      <div className="flex items-start gap-2 rounded-lg bg-muted/50 p-3 text-xs text-muted-foreground">
        <Info className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
        <span>{bindingMonths === 12 ? t("order.binding12") : t("order.binding3")}</span>
      </div>
    </div>
  );
}
